/**
 * Calf KPI Service
 * Calculates key performance indicators for calf health management
 *
 * KPIs: Mortality rate, ADG, Vaccination compliance, Disease incidence,
 *        Scours rate, Average Wisconsin score
 */

import { calculateADG } from "./weightTrackingService";
import { calculateVaccinationCompliance } from "./vaccinationService";

// ============================================================================
// KPI CALCULATIONS
// ============================================================================

/**
 * Calculate all calf KPIs for a farm
 * @param {Object} params
 * @param {Array} params.animals - All animals
 * @param {Array} params.weightRecords - All weight records
 * @param {Array} params.diseaseRecords - All disease records
 * @param {Array} params.vaccinations - All vaccination records
 * @param {Array} params.vaccinationTemplates - Active templates
 * @param {Object} params.settings - CalfERP settings (with thresholds)
 * @param {Object} params.period - { startDate, endDate } (optional filter)
 * @returns {Object} KPI results
 */
export function calculateCalfKPIs({
	animals = [],
	weightRecords = [],
	diseaseRecords = [],
	vaccinations = [],
	vaccinationTemplates = [],
	settings = {},
	period = null,
} = {}) {
	const thresholds = settings?.kpiThresholds || {};

	// Filter by period if specified
	const filterByPeriod = (records) => {
		if (!period) return records;
		const start = period.startDate ? new Date(period.startDate).getTime() : 0;
		const end = period.endDate ? new Date(period.endDate).getTime() : Infinity;
		return records.filter((r) => r.t >= start && r.t <= end);
	};

	const periodDiseases = filterByPeriod(diseaseRecords);
	const periodVaccinations = filterByPeriod(vaccinations);

	// Mortality rate
	const mortality = calculateMortalityRate(animals, period);

	// Average ADG across all animals with weight data
	const avgADG = calculateAverageADG(animals, weightRecords);

	// Vaccination compliance
	const vacCompliance = calculateFarmVaccinationCompliance(
		animals,
		periodVaccinations,
		vaccinationTemplates
	);

	// Disease incidence
	const diseaseIncidence = calculateDiseaseIncidence(periodDiseases, animals.length);

	// Scours rate
	const scoursRate = calculateScoursRate(periodDiseases, animals.length);

	// BRD rate
	const brdRate = calculateBRDRate(periodDiseases, animals.length);

	// Average Wisconsin score
	const avgWisconsin = calculateAverageWisconsinScore(periodDiseases);

	return {
		mortalityRate: {
			value: mortality.rate,
			count: mortality.count,
			total: mortality.total,
			status: getKPIStatus(mortality.rate, thresholds.mortalityRate, true),
		},
		averageADG: {
			value: avgADG.average,
			count: avgADG.animalsWithData,
			status: getKPIStatus(avgADG.average, thresholds.adg, false),
		},
		vaccinationCompliance: {
			value: vacCompliance.percent,
			completed: vacCompliance.completed,
			total: vacCompliance.total,
			status: getKPIStatus(vacCompliance.percent, thresholds.vaccinationCompliance, false),
		},
		diseaseIncidence: {
			value: diseaseIncidence,
			status: getKPIStatus(diseaseIncidence, thresholds.scoursIncidence, true),
		},
		scoursRate: {
			value: scoursRate,
			status: getKPIStatus(scoursRate, thresholds.scoursIncidence, true),
		},
		brdRate: {
			value: brdRate,
			status: getKPIStatus(brdRate, thresholds.brdIncidence, true),
		},
		averageWisconsinScore: {
			value: avgWisconsin,
			status: getKPIStatus(avgWisconsin, thresholds.avgWisconsinScore, true),
		},
	};
}

/**
 * Calculate KPIs for fattening cattle (feedlot view)
 * Same input contract as calculateCalfKPIs, but with fattening-relevant
 * metrics (days on feed, lameness instead of scours/Wisconsin) and
 * thresholds from the fattening ERP settings section.
 *
 * @param {Object} params - Same shape as calculateCalfKPIs
 * @returns {Object} KPI results
 */
export function calculateFatteningKPIs({
	animals = [],
	weightRecords = [],
	diseaseRecords = [],
	vaccinations = [],
	vaccinationTemplates = [],
	settings = {},
	period = null,
} = {}) {
	const thresholds = settings?.kpiThresholds || {};

	const filterByPeriod = (records) => {
		if (!period) return records;
		const start = period.startDate ? new Date(period.startDate).getTime() : 0;
		const end = period.endDate ? new Date(period.endDate).getTime() : Infinity;
		return records.filter((r) => r.t >= start && r.t <= end);
	};

	const periodDiseases = filterByPeriod(diseaseRecords);
	const periodVaccinations = filterByPeriod(vaccinations);

	const mortality = calculateMortalityRate(animals, period);
	const avgADG = calculateAverageADG(animals, weightRecords);
	const vacCompliance = calculateFarmVaccinationCompliance(
		animals,
		periodVaccinations,
		vaccinationTemplates
	);
	const brdRate = calculateBRDRate(periodDiseases, animals.length);
	const lamenessRate = calculateCategoryRate(periodDiseases, animals.length, "lameness");
	const daysOnFeed = calculateAverageDaysOnFeed(animals);

	return {
		mortalityRate: {
			value: mortality.rate,
			count: mortality.count,
			total: mortality.total,
			status: getKPIStatus(mortality.rate, thresholds.mortalityRate, true),
		},
		averageADG: {
			value: avgADG.average,
			count: avgADG.animalsWithData,
			status: getKPIStatus(avgADG.average, thresholds.adg, false),
		},
		vaccinationCompliance: {
			value: vacCompliance.percent,
			completed: vacCompliance.completed,
			total: vacCompliance.total,
			status: getKPIStatus(vacCompliance.percent, thresholds.vaccinationCompliance, false),
		},
		brdRate: {
			value: brdRate,
			status: getKPIStatus(brdRate, thresholds.brdIncidence, true),
		},
		lamenessRate: {
			value: lamenessRate,
			status: getKPIStatus(lamenessRate, thresholds.lamenessIncidence, true),
		},
		averageDaysOnFeed: {
			value: daysOnFeed.average,
			count: daysOnFeed.animalsWithData,
			// Informational metric — days on feed has no good/bad band of its own
			status: "unknown",
		},
	};
}

// ============================================================================
// INDIVIDUAL CALCULATIONS
// ============================================================================

/**
 * Average days since stocking-in (EntryDate) across animals that have one.
 * @param {Array} animals
 * @returns {{ average: number|null, animalsWithData: number }}
 */
function calculateAverageDaysOnFeed(animals) {
	const now = Date.now();
	const dayMs = 24 * 60 * 60 * 1000;

	let totalDays = 0;
	let count = 0;

	for (const animal of animals) {
		if (!animal.EntryDate) continue;
		const entryMs =
			typeof animal.EntryDate === "number"
				? animal.EntryDate
				: new Date(animal.EntryDate).getTime();
		if (isNaN(entryMs) || entryMs > now) continue;
		totalDays += Math.floor((now - entryMs) / dayMs);
		count++;
	}

	return {
		average: count > 0 ? Math.round(totalDays / count) : null,
		animalsWithData: count,
	};
}

function calculateCategoryRate(diseaseRecords, totalAnimals, category) {
	if (!totalAnimals) return 0;
	const affected = new Set(
		diseaseRecords.filter((r) => r.diseaseCategory === category).map((r) => r.MAC)
	).size;
	return Math.round((affected / totalAnimals) * 1000) / 10;
}

function calculateMortalityRate(animals, period) {
	if (!animals.length) return { rate: 0, count: 0, total: 0 };

	const deceased = animals.filter((a) => {
		if (!a.deathDate && !a.ArchivedReason?.toLowerCase().includes("death")) return false;
		if (!period) return true;
		const deathTs = a.deathDate || a.ArchivedAt;
		return deathTs >= period.startDate && deathTs <= period.endDate;
	});

	return {
		rate: Math.round((deceased.length / animals.length) * 1000) / 10, // percentage, 1 decimal
		count: deceased.length,
		total: animals.length,
	};
}

function calculateAverageADG(animals, weightRecords) {
	const animalWeights = {};
	for (const wr of weightRecords) {
		if (!animalWeights[wr.MAC]) animalWeights[wr.MAC] = [];
		animalWeights[wr.MAC].push(wr);
	}

	let totalADG = 0;
	let count = 0;

	for (const animal of animals) {
		const records = animalWeights[animal.MAC];
		if (!records || records.length < 2) continue;

		const sorted = records.sort((a, b) => a.t - b.t);
		const { adg } = calculateADG(sorted, animal.birthWeight, animal.birthDate);
		if (adg != null && adg > 0) {
			totalADG += adg;
			count++;
		}
	}

	return {
		average: count > 0 ? Math.round((totalADG / count) * 1000) / 1000 : null,
		animalsWithData: count,
	};
}

function calculateFarmVaccinationCompliance(animals, vaccinations, templates) {
	if (!templates.length || !animals.length) {
		return { percent: 100, completed: 0, total: 0 };
	}

	let totalRequired = 0;
	let totalCompleted = 0;

	for (const animal of animals) {
		const animalVax = vaccinations.filter((v) => v.MAC === animal.MAC);
		for (const template of templates) {
			const { completed, total } = calculateVaccinationCompliance(animalVax, template);
			totalRequired += total;
			totalCompleted += completed;
		}
	}

	return {
		percent: totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 100,
		completed: totalCompleted,
		total: totalRequired,
	};
}

function calculateDiseaseIncidence(diseaseRecords, totalAnimals) {
	if (!totalAnimals) return 0;
	const uniqueAnimals = new Set(diseaseRecords.map((r) => r.MAC)).size;
	return Math.round((uniqueAnimals / totalAnimals) * 1000) / 10;
}

function calculateScoursRate(diseaseRecords, totalAnimals) {
	if (!totalAnimals) return 0;
	const scoursAnimals = new Set(
		diseaseRecords.filter((r) => r.diseaseCategory === "scours").map((r) => r.MAC)
	).size;
	return Math.round((scoursAnimals / totalAnimals) * 1000) / 10;
}

function calculateBRDRate(diseaseRecords, totalAnimals) {
	if (!totalAnimals) return 0;
	const brdAnimals = new Set(
		diseaseRecords.filter((r) => r.diseaseCategory === "brd").map((r) => r.MAC)
	).size;
	return Math.round((brdAnimals / totalAnimals) * 1000) / 10;
}

function calculateAverageWisconsinScore(diseaseRecords) {
	const scored = diseaseRecords.filter((r) => r.wisconsinScoreTotal != null);
	if (!scored.length) return 0;
	const total = scored.reduce((sum, r) => sum + r.wisconsinScoreTotal, 0);
	return Math.round((total / scored.length) * 10) / 10;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Determine KPI status (good/warning/critical) based on thresholds.
 * Exported for KPIs graded outside the calculators (e.g. the FCR card,
 * whose value is computed in the dashboard from feed + weight data).
 * @param {number} value
 * @param {Object} threshold - { good, warning, critical }
 * @param {boolean} lowerIsBetter - true for rates (mortality, disease), false for ADG/compliance
 * @returns {string} "good" | "warning" | "critical" | "unknown"
 */
export function getKPIStatus(value, threshold, lowerIsBetter) {
	if (value == null || !threshold) return "unknown";

	if (lowerIsBetter) {
		if (value <= threshold.good) return "good";
		if (value <= threshold.warning) return "warning";
		return "critical";
	} else {
		if (value >= threshold.good) return "good";
		if (value >= threshold.warning) return "warning";
		return "critical";
	}
}
